import { forEach } from 'lodash-es';
import { isSissCloudExtraData } from '../sign/siss-cloud/siss-cloud.models';

/**
 * Tipi di firma
 * */
export enum SignType {
    Undefined = -1,
    CompedDigitalSign = 0,
    SISSWay = 1,
    SISS = 2,
    ARUBA = 3,
    InfoCert = 4,
    ScrybaSign = 5, // non ancora supportata nel web
    Confirmo = 6,
    SISSWayCloud = 7,
    SISSCloud = 8,
    InsielARSS = 9,
}

export enum ScrybaSignOtpType {
    C100 = 0,
    YUBICO8 = 1,
    SMS = 2,
    ARUBACALL = 3,
    APP = 4,
}

export const signTypeToNameMap: Record<
    SignType,
    | 'aruba'
    | 'comped'
    | 'sissWay'
    | 'siss'
    | 'infoCert'
    | 'confirmo'
    | 'scrybaSign'
    | 'sissWayCloud'
    | 'sissCloud'
    | 'insielARSS'
    | 'undefined'
> = {
    [SignType.Undefined]: 'undefined',
    [SignType.ARUBA]: 'aruba',
    [SignType.CompedDigitalSign]: 'comped',
    [SignType.SISSWay]: 'sissWay',
    [SignType.SISS]: 'siss',
    [SignType.InfoCert]: 'infoCert',
    [SignType.Confirmo]: 'confirmo',
    [SignType.ScrybaSign]: 'scrybaSign',
    [SignType.SISSWayCloud]: 'sissWayCloud',
    [SignType.SISSCloud]: 'sissCloud',
    [SignType.InsielARSS]: 'insielARSS',
};

export interface EcgChartState {
    cabrera: boolean;
    disposition: number;
    markerDisplay: boolean;
    paperOpacity: number;
    paperSpeed: number;
    sequential: boolean;
    space: number;
    printAsDisplayed: boolean;
    // ReportKind: "page_1",
    // Rhythm: 4
}

export interface IBaseSignInfo {
    signerName: string;
    signerId: string;
    pin: string;
}

export interface IBaseSignInfoWithOtp extends IBaseSignInfo {
    otp?: string;
    openedSessionId?: string;
}

export interface ISeSignInfo extends IBaseSignInfo {
    reportIdList: string[];

    resetData();
}

export interface ISeSignInfoWithOtp extends ISeSignInfo {
    otp?: string;
    openedSessionId?: string;
}

export interface ISeMultiSignInfo extends IBaseSignInfoWithOtp {
    reportIdList: string[];
    multiSignerInfo: IBaseSignInfoWithOtp[];
}

export const signInfoIsSet = (seSignInfo: ISeSignInfo): boolean => Boolean(seSignInfo.pin && seSignInfo.signerId);

export class SeArubaSignInfo implements ISeSignInfo {
    signerName: string;
    signerId: string;
    pin: string;
    reportIdList: string[];

    otp: string;
    openedSessionId: string; // Se nullo apre la sessione

    constructor(data: SeSignRequestData) {
        this.signerName = data.signerName;
        this.signerId = data.signerId;
        this.pin = '';
        this.reportIdList = data.reportIdList;
    }

    resetData(): any {
        this.signerName = '';
        this.signerId = '';
        this.pin = '';
        this.reportIdList = [];
        this.otp = '';
        this.openedSessionId = '';
    }
}

export class SeInsielARSSSignInfo implements ISeSignInfo {
    signerName: string;
    signerId: string;
    pin: string;
    reportIdList: string[];

    otp: string;
    openedSessionId: string; // Se nullo apre la sessione

    constructor(data: SeSignRequestData) {
        this.signerName = data.signerName;
        this.signerId = data.signerId;
        this.pin = '';
        this.reportIdList = data.reportIdList;
    }

    resetData(): any {
        this.signerName = '';
        this.signerId = '';
        this.pin = '';
        this.reportIdList = [];
        this.otp = '';
        this.openedSessionId = '';
    }
}

export class SeSissSignInfo implements ISeSignInfo {
    signerName: string;
    signerId: string;
    signerCode?: string; // codice firmatario (cf) associato al signerId
    pin: string;
    reportIdList: string[];

    docDataArray: ISeSignResultData[];

    documenti: IDocumentoSiss[]; // è lo 'specchio' della sequenza 'documenti' nell'xml siss\sissway

    enableTimestamp: boolean = false;

    constructor(data: SeSissSignRequestData) {
        this.resetData();

        this.signerName = data.signerName;
        this.signerId = data.signerId;
        this.pin = '';
        this.docDataArray = [];

        this.reportIdList = data.reportIdList;
        // Costruisco lo scheletro dei valori di ritorno
        // e per prima cosa mappo gli id dei documenti da firmare
        // sulla struttura di ritorno
        forEach(this.reportIdList, (id) => {
            this.docDataArray.push({
                reportId: id,
                signed: false,
            });
        });

        // inserisco tanti documenti quanti sono i report divisi per paziente (con ripetizioni)
        this.documenti = [];
        forEach(data.sissDataInfoList, (info: ISissDataInfo) => {
            info.report.forEach((report) => {
                this.documenti.push({
                    obscurationInfo: info.obscuration,
                    patientInfo: info.patient,
                    reportInfo: report,
                });
            });
        });
    }

    resetData(): any {
        this.signerName = '';
        this.signerId = '';
        this.signerCode = '';
        this.pin = '';
        this.reportIdList = [];

        this.docDataArray = [];

        this.documenti = [];
    }
}

export class SeCompedSignInfo implements ISeSignInfo {
    signerName: string;
    signerId: string; // guid utente estensa che firma
    signerCode?: string; // codice firmatario (cf) associato al signerId
    pin: string;
    reportIdList: string[];

    docDataArray: ISeSignResultData[];
    enableTimestamp: boolean;
    isSecondSigner: boolean = false;

    constructor(data: SeSignRequestData) {
        this.signerName = data.signerName;
        this.signerId = data.signerId;
        this.pin = '';
        this.enableTimestamp = false;
        this.docDataArray = [];

        this.reportIdList = data.reportIdList;
        // Costruisco lo scheletro dei valori di ritorno
        // e per prima cosa mappo gli id dei documenti da firmare
        // sulla struttura di ritorno
        forEach(this.reportIdList, (id) => {
            this.docDataArray.push({
                reportId: id,
                signed: false,
            });
        });
    }

    resetData(): any {
        this.signerName = '';
        this.signerId = '';
        this.signerCode = '';
        this.pin = '';
        this.reportIdList = [];
        this.enableTimestamp = false;
        this.docDataArray = [];
    }
}

export class SeInfoCertSignInfo implements ISeSignInfo {
    signerName: string;
    signerId: string;
    pin: string;
    reportIdList: string[];

    constructor(data: SeSignRequestData) {
        this.signerName = data.signerName;
        this.signerId = data.signerId;
        this.pin = '';
        this.reportIdList = data.reportIdList;
    }

    resetData(): any {
        this.signerName = '';
        this.signerId = '';
        this.pin = '';
        this.reportIdList = [];
    }
}

export class SeConfirmoSignInfo implements ISeSignInfo {
    signerName: string;
    signerId: string;
    pin: string;
    reportIdList: string[];

    // new middleware
    password: string;
    otp: string;
    deviceId: string;
    username: string;

    hasPassword: boolean;
    hasOtp: boolean;
    hasResetPin: boolean;
    hasDeviceId: boolean;

    devices: SeConfirmoDevice[];

    constructor(data: SeSignRequestData) {
        this.signerName = data.signerName;
        this.signerId = data.signerId;
        this.reportIdList = data.reportIdList;

        this.pin = '';
        this.otp = '';

        let tmp: SeConfirmoSignInfo = data.extraData as SeConfirmoSignInfo;
        this.password = tmp.password;
        this.deviceId = tmp.deviceId;
        this.username = tmp.username;

        this.hasPassword = tmp.hasPassword;
        this.hasDeviceId = tmp.hasDeviceId;
        this.hasOtp = tmp.hasOtp;
        this.hasResetPin = tmp.hasResetPin;

        this.devices = tmp.devices ?? [];
    }

    resetData(): any {
        this.signerName = '';
        this.signerId = '';
        this.pin = '';
        this.reportIdList = [];

        // new middleware
        this.password = '';
        this.otp = '';
        this.deviceId = '';
        this.username = '';

        this.hasPassword = false;
        this.hasDeviceId = false;
        this.hasOtp = false;
        this.hasResetPin = false;

        this.devices = [];
    }
}

interface SeConfirmoDevice {
    description: string;
    deviceId: string;
}

class SeSissCloudSignInfoBase implements ISeSignInfo {
    // Da interfaccia
    reportIdList: string[];
    // AlternativeUserName
    signerName: string;
    // UserId
    signerId: string;
    // Password
    pin: string;

    //Specifici SISSCloud
    signerDomain: string;
    signerUsername: string;
    otp: string;
    token: string;
    sessionId: string;
    errors: Record<string, string>;
    serverFileNames: Record<string, string>;
    reportDescrList: IDocumentoSiss[];
    documents: IDocumentoSiss[];

    constructor(data: SeSissCloudSignRequestData) {
        assertIsSissCloudSignRequestData(data);
        this.signerName = data.signerName;
        this.signerId = data.signerId;
        this.pin = '';
        this.reportIdList = data.reportIdList;
        this.reportDescrList = data.reportErrorDetailList.map((descr: any) => descr.message);
        this.buildDocuments(data);
    }

    resetData() {
        this.reportIdList = [];
        this.signerName = '';
        this.signerId = '';
        this.pin = '';
        this.signerDomain = '';
        this.signerUsername = '';
        this.otp = '';
        this.token = '';
        this.sessionId = '';
        this.errors = {};
        this.serverFileNames = {};
        this.reportDescrList = [];
        this.documents = [];
    }

    protected buildDocuments(data: SeSissCloudSignRequestData) {
        this.documents = data.sissDataInfoList
            .map((info) =>
                info.report.map((report) => ({
                    obscurationInfo: info.obscuration,
                    patientInfo: info.patient,
                    reportInfo: report,
                })),
            )
            .flat();
    }
}

export class SeSissCloudSignInfo extends SeSissCloudSignInfoBase {
    constructor(data: SeSignRequestData) {
        assertIsSissCloudSignRequestData(data);
        super(data);

        if (isSissCloudExtraData(data.extraData)) {
            if (data.signerName.includes('\\')) {
                [this.signerDomain, this.signerUsername] = data.signerName.split('\\');
            } else {
                this.signerDomain = '';
                this.signerUsername = data.signerName;
            }

            this.sessionId = data.extraData.sessionId;
        }
    }
}

export class SeSissWayCloudSignInfo extends SeSissCloudSignInfoBase {
    constructor(data: SeSignRequestData) {
        assertIsSissCloudSignRequestData(data);
        super(data);

        if (isSissCloudExtraData(data.extraData)) {
            if (data.signerName.includes('\\')) {
                [this.signerDomain, this.signerUsername] = data.signerName.split('\\');
            } else {
                this.signerDomain = '';
                this.signerUsername = data.signerName;
            }

            this.sessionId = data.extraData.sessionId;
        }
    }
}

export class SeScrybaSignInfo implements ISeSignInfo {
    signerName: string;
    signerId: string;
    pin: string;
    otp: string;
    reportIdList: string[];
    sessionId: string;

    certificates: IScrybaCertificate[];
    selectedCertificate: string | null;

    constructor(data: SeSignRequestData) {
        this.signerName = data.signerName;
        this.signerId = data.signerId;
        this.pin = '';
        this.otp = '';
        this.sessionId = '';
        this.reportIdList = data.reportIdList;
        this.certificates = data.extraData as IScrybaCertificate[];
        this.selectedCertificate =
            !!this.certificates && this.certificates.length >= 1 ? this.certificates[0].code : null;
    }

    resetData(): any {
        this.signerName = '';
        this.signerId = '';
        this.pin = '';
        this.reportIdList = [];
        this.sessionId = '';

        this.certificates = [];
        this.selectedCertificate = null;
    }
}

export interface IScrybaCertificate {
    code: string;
    description: string;
    otPtypeList: string[];
}

export interface SeScrybaSignDespatchOtpInfo {
    signerId: string;
    pin: string;
    otpType: string;
    certId: string;
}

/**
 * interfaccia mappata sui dati che si aspetta il backend per la multi firma
 *  public class SeSignInfo in
 * vedi D:\projects\EBIT\TestProject22\Web\Estensa.Web.Rest\Models\Sign vNext\ISeSignInfo.cs
 */
export type SeMultiSignInfo = {
    type: SignType;
    isDeferred?: boolean;
    displayedSettings?: EcgChartState;
    aruba?: ISeMultiSignInfo;
    comped?: ISeMultiSignInfo;
    sissWay?: ISeMultiSignInfo;
    siss?: ISeMultiSignInfo;
    infoCert?: ISeMultiSignInfo;
    confirmo?: ISeMultiSignInfo;
};

export class SeSignInfo {
    aruba: SeArubaSignInfo;
    comped: SeCompedSignInfo;
    sissWay: SeSissSignInfo;
    siss: SeSissSignInfo;
    infoCert: SeInfoCertSignInfo;
    confirmo: SeConfirmoSignInfo;
    insielARSS: SeInsielARSSSignInfo;
    sissCloud: SeSissCloudSignInfo;
    sissWayCloud: SeSissWayCloudSignInfo;
    scryba: SeScrybaSignInfo;
    type: SignType;
    isDeferred?: boolean;
    displayedSettings?: EcgChartState;

    isVisitAndEcgSign: boolean = false;
    visitCount: number = 0;
    ecgCount: number = 0;

    get info(): ISeSignInfo | null {
        switch (this.type) {
            case SignType.ARUBA:
                return this.aruba;
            case SignType.CompedDigitalSign:
                return this.comped;
            case SignType.SISSWay:
                return this.sissWay;
            case SignType.SISS:
                return this.siss;
            case SignType.InfoCert:
                return this.infoCert;
            case SignType.Confirmo:
                return this.confirmo;
            case SignType.InsielARSS:
                return this.insielARSS;
            case SignType.SISSCloud:
                return this.sissCloud;
            case SignType.SISSWayCloud:
                return this.sissWayCloud;
            case SignType.ScrybaSign:
                return this.scryba;
            default:
                return null;
        }
    }
}

/**
 * Contiene le informazioni che vengono scambiate tra client e server.
 * Descrive l'esito dell'operazione e contiene il risultato della firma
 * */
export interface ISeSignResultData {
    reportId: string; // Id del documento du DB
    reportDescr?: string; //Un descrittivo che identifichi il documento (utile per indicare all'utente quale ha avuto errori)
    signed: boolean; // Indica se è stato firmato o no (utile nelle operazioni di firma multipla)
    uploaded?: boolean; // Per le firme che richiedono uno step prima della firma, indica se i documenti sono stati caricati verso terzi.
    isGenericError?: boolean; // segnala errore non di firma. timeout, pdf generation error, rabbit...
    serverError?: string; // Contiene eventuale messaggio di errore (molto utile nelle operazioni di firma multipla perchè riusciamo a estrapolare eventuali info relative all'errore sulla firma del singolo doc))
    serverErrorDetail?: string; // Info dettagliate sull'errore
    serverErrorCode?: string; // Codice di errore (per usi interni)
    signedDocByteArray?: string; // stringa base64 relativa al byte[] del documento firmato
    srcDocByteArray?: string; // stringa base64 relativa al byte[] del documento da firmare
    signedDocXml?: string; // stringa relativa all'xml del documento firmato
    signSessionId?: string; // Se sto firmando con aruba contiene l'id della sessione di ferma utilizzata
    signerId?: string; //id del firmatario, serve nel caso di errore su multifirma per sapere chi ha sbagliato il pin/otp
    signerCode?: string; // Codice Fiscale per CompEd
    signDate?: string; // Data di firma (quella che poi viene inserita nel PDF) - Serve per CompEd, sulle firme web è contestuale alla chiamata su REST
    previousSignDate?: string; // Data di firma (quella che poi viene inserita nel PDF) - Serve per CompEd, sulle firme web è contestuale alla chiamata su REST
    p7mVersion?: number; // Data di firma (quella che poi viene inserita nel PDF) - Serve per CompEd, sulle firme web è contestuale alla chiamata su REST
    identificativoDocumento?: string; // SISS
    identificativoDAO?: string; // SISS;
    publishInfo?: string; // SISS-SissWay;
    cda2ByteArray?: string; // DCE in formato CDA2 per la firma SISS (inserito per scopi futuri)
    signedDAOByteArray?: string; // stringa base64 relativa al byte[] del documento firmato
    report?: any;
    cdaFlag?: ICDAFlag;
    significantImagesInfo?: ISignificantImagesInfo;
}

/**
 * Tipi di report (viene usato nel siss e nel siss way)
 * */
export enum ReportType {
    SpecialisticReport = 0,
    SpecialisticRadiologicalReport = 1,
    ClinicalDischargeLetter = 2,
    Unknown = 3,
}

/**
 * Gestisce le informazioni necessarie ad avviare il processo di firma
 * che solitamente vengono passate dal modulo che richiede la firma
 * */
export interface SeSignRequestData {
    signerName: string;
    signerId: string;
    reportIdList: string[]; // Lista degli identificativi dei documenti da firmare
    initialErrorMessage?: string; // Eventuale messaggio di errore iniziale
    reportErrorDetailList?: any; // Informazioni sul report da ripostare nei messaggi di errore (per non usare l'id!!)
    extraData?: unknown; // Dati da mantenere tra le varie operazioni - specifiche per ogni tipo di firma.
}

export interface SeSissSignRequestData extends SeSignRequestData {
    sissDataInfoList: ISissDataInfo[];
}

export interface SeSissCloudSignRequestData extends SeSignRequestData {
    sissDataInfoList: ISissDataInfo[];
}

export function isSissCloudSignRequestData(data: SeSignRequestData): data is SeSissCloudSignRequestData {
    if (!('sissDataInfoList' in data)) {
        return false;
    }
    return true;
}

export function assertIsSissCloudSignRequestData(data: SeSignRequestData): asserts data is SeSissCloudSignRequestData {
    if (!isSissCloudSignRequestData(data)) {
        throw new Error('Invalid data provided to SissCloud sign');
    }
}

export interface ISissDataInfo {
    patient: ISissPatientInfo;
    obscuration: ISissObscurationInfo;
    report: ISissReportInfo[]; //Nell'ecg ne abbiamo solo uno, ma metto l'array se servisse
}

export interface SissData {
    patient: SissPatient;
    obscuration: SissObscuration;
    examination: SissExamination;
}

export interface SissObscuration {
    obsDrug: boolean;
    obsHiv: boolean;
    obsViolence: boolean;
    obsInterruption: boolean;
    obsVoluntary: boolean;
    obsAvailability: string;
    consultationAuthorized: boolean;
}

export interface SissPatient {
    birthDate?: Date;
    healthCard: string;
    name: string;
    surname: string;
    sexCode: string;
    taxCode: string;
}

export interface SissExamination {
    admissionDate?: Date;
    examCode?: string; // SissWay
    nosologicNumberHospitalization: string;
    fileNumberER: string;
    idPrestazioneSISS?: string; // Siss
    descrizionePrestazioneSISS?: string; // Siss
    iup?: string; // Siss
}

/**
 * Gestisce le informazioni storate nel servizio di firma
 * che vengono utilizzate per evitare la richiesta delle credenziali
 * */
export interface StoredSeSignInfo {
    signerId: string;
    signerName: string;
    pin: string;
    otp?: string;
}

export interface IDocumentoSiss {
    reportInfo: ISissReportInfo;
    patientInfo: ISissPatientInfo;
    obscurationInfo: ISissObscurationInfo;
}

export interface ISissReportInfo {
    id: string;

    reportType: ReportType;
    nosologicNumberHospitalization: string;
    fileNumberER: string;
    admissionDateHospitalization: string;

    codesCardioPerform?: string[]; // SOLO SISSWAY

    idPrestazioneSISS?: string; // SOLO SISS
    descrizionePrestazioneSISS?: string; // SOLO SISS
    iup?: string; // SOLO SISS
}

export interface ISissPatientInfo {
    id: string;
    healthCard: string;
    taxCode: string;
    name: string;
    surname: string;
    sexCode: string;
    birthDate: string;
    isLocalPatient: boolean;
}

export interface ISissObscurationInfo {
    obsDrug: boolean;
    obsHiv: boolean;
    obsViolence: boolean;
    obsInterruption: boolean;
    obsVoluntary: boolean;
    obsAvailability: string;
    consultationAuthorized: boolean;
}

export class SignPartialSuccessDetailedError extends Error {
    constructor(
        readonly total: number,
        readonly numSigned: number,
        readonly details: string[],
    ) {
        super('SignPartialSuccessDetailedError');
    }
}

export interface ICDAFlag {
    cdaInjected: boolean;
    cdaValidated: boolean;
}

export interface ISignificantImagesInfo {
    significantImagesOnPacs: number;
    maxSignificantImagesToAdd: number;
}

